/* =============================================================================
   Layer stack — the ordering brain of the overlay engine (strategy §14.4).

   A singleton registry of every open overlay layer, in mount order. It answers
   ONE question the divergent implementations couldn't: "am I the top layer?"
   Esc and outside-press must dismiss only the topmost layer; before this,
   overlays fought each other with stopPropagation (see Select's capture-phase
   Esc handler, now retired).

   Framework-free on purpose: testable without React, consumed via useLayer.
   NOT part of the public API — with one deliberate exception: the narrow query
   `hasOpenOverlayLayer()` is re-exported from the root barrel (PB-356) so modules
   can defer their own Esc handlers to the overlay engine without importing the
   raw stack. The stack itself stays internal.
   ========================================================================== */

export interface LayerEntry {
  id: string;
  /** Modal layers get scrim + scroll-lock + inert background via useLayer. */
  modal: boolean;
  /** Passive tooltip layers may be ignored by authoring commands, but still handle Escape. */
  kind?: "tooltip" | "reading" | undefined;
}

type Listener = () => void;

const stack: LayerEntry[] = [];
const listeners = new Set<Listener>();

function notify(): void {
  for (const l of listeners) l();
}

/* ── Event-start record (SHR-BUG-7) ──────────────────────────────────────────
   Each open layer has its own document listener, so one press reaches several.
   In a real browser React commits between two listeners of one user event, so
   a layer that closes in the first one is already gone from the stack when the
   next asks "am I on top?" — and the layer under it closed on the same press
   (Escape on a calendar also closed its dialog). A window capture listener
   runs before every document listener; it records the stack as each keydown
   and pointerdown STARTS, and dismissal asks that record. */
let eventStacks = new WeakMap<Event, readonly LayerEntry[]>();
let recording = false;

function recordEventStart(event: Event): void {
  eventStacks.set(event, stack.slice());
}

function startRecording(): void {
  if (recording) return;
  recording = true;
  window.addEventListener("keydown", recordEventStart, true);
  window.addEventListener("pointerdown", recordEventStart, true);
}

export function registerLayer(entry: LayerEntry): void {
  startRecording();
  // Re-registering an id moves it to the top (covers re-open races).
  const i = stack.findIndex((e) => e.id === entry.id);
  if (i !== -1) stack.splice(i, 1);
  stack.push(entry);
  notify();
}

export function unregisterLayer(id: string): void {
  const i = stack.findIndex((e) => e.id === id);
  if (i === -1) return;
  stack.splice(i, 1);
  notify();
}

/** Is this layer topmost? `ignoreTooltips` looks past passive tooltip layers
 *  above it, as {@link hasOpenOverlayLayer} does. */
export function isTopLayer(
  id: string,
  options?: { ignoreTooltips?: boolean },
): boolean {
  return isTopOf(stack, id, options);
}

/** Was this layer topmost when `event` STARTED? An event with no record (one
 *  dispatched before any layer opened) is answered by the live stack. */
export function wasTopLayerAtEventStart(
  event: Event,
  id: string,
  options?: { ignoreTooltips?: boolean },
): boolean {
  return isTopOf(eventStacks.get(event) ?? stack, id, options);
}

function isTopOf(
  entries: readonly LayerEntry[],
  id: string,
  options?: { ignoreTooltips?: boolean },
): boolean {
  for (let index = entries.length - 1; index >= 0; index -= 1) {
    const entry = entries[index];
    const passiveTooltip = !entry?.modal && entry?.kind === "tooltip";
    if (options?.ignoreTooltips && passiveTooltip) continue;
    return entry?.id === id;
  }
  return false;
}

/** The highest modal in the stack, ignoring non-modal helpers above it. */
export function topModalLayerId(): string | null {
  for (let index = stack.length - 1; index >= 0; index -= 1) {
    const entry = stack[index];
    if (entry?.modal) return entry.id;
  }
  return null;
}

export function hasModalLayer(): boolean {
  return stack.some((e) => e.modal);
}

export function layerCount(): number {
  return stack.length;
}

/**
 * Public (root-barrel) query: is any overlay layer currently open? PB-356 — a
 * module-level `keydown` handler (e.g. VSM's map-Escape) calls this to yield to
 * the overlay engine: when a DS overlay is open the module must NOT act on Esc,
 * letting the topmost layer dismiss first. Narrow by design so the raw stack
 * stays encapsulated.
 */
export function hasOpenOverlayLayer(options?: {
  ignoreTooltips?: boolean;
  ignoreReading?: boolean;
}): boolean {
  return stack.some((layer) => {
    if (layer.modal) return true;
    if (options?.ignoreTooltips && layer.kind === "tooltip") return false;
    if (options?.ignoreReading && layer.kind === "reading") return false;
    return true;
  });
}

export function subscribeLayers(fn: Listener): () => void {
  listeners.add(fn);
  return () => listeners.delete(fn);
}

/* ── Portal root ─────────────────────────────────────────────────────────────
   All portaled overlay content mounts into one dedicated element so the
   "outside press" test and future inert handling have a stable boundary. */

const LAYER_ROOT_ATTR = "data-pbds-layer-root";

export function getLayerRoot(): HTMLElement {
  let root = document.querySelector<HTMLElement>(`[${LAYER_ROOT_ATTR}]`);
  if (!root) {
    root = document.createElement("div");
    root.setAttribute(LAYER_ROOT_ATTR, "");
    document.body.appendChild(root);
  }
  return root;
}

/** Test hook — resets module state between specs. Not for runtime use. */
export function __resetLayerStack(): void {
  stack.length = 0;
  listeners.clear();
  eventStacks = new WeakMap();
}
